import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock3, ArrowRight, CircleAlert } from "lucide-react";
import { getBookingPayment, getBookingStatus } from "../lib/api";
import { formatAppointment } from "../lib/datetime";
import { CANCEL_POLICY_SHORT } from "../lib/legal";
import { EDITORIAL_IMAGES } from "../lib/editorialImages";

const Eyebrow = ({ children, color = "text-mocha" }) => (
  <p className={`text-[11px] tracking-[0.22em] uppercase ${color}`}>{children}</p>
);

const HERO = EDITORIAL_IMAGES.detail;

export default function BookingConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const [state, setState] = useState({ kind: "polling", booking: null, attempts: 0 });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId && !bookingId) {
      setState({ kind: "error", message: "Missing booking reference" });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const b = sessionId ? await getBookingStatus(sessionId) : await getBookingPayment(bookingId);
        if (cancelled) return;
        if (b.status === "authorized" || b.status === "captured" || b.status === "confirmed") {
          setState({ kind: "success", booking: b });
          return;
        }
        if (b.status === "expired") {
          setState({ kind: "expired", booking: b });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setState({ kind: "timeout", booking: b });
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        setState({ kind: "error", message: err?.response?.data?.detail || "Could not verify booking." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, bookingId]);

  return (
    <div className="pt-16 min-h-screen flex items-center" data-testid="booking-confirmed-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-20 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7 reveal">
          {state.kind === "polling" && (
            <>
              <Eyebrow index="—">Authorising</Eyebrow>
              <h1 className="mt-8 font-serif text-6xl md:text-7xl leading-[0.9] tracking-editorial">
                Holding your<br /><span className="italic text-espresso">chair.</span>
              </h1>
              <p className="mt-8 text-ink/70 max-w-lg leading-relaxed">
                Verifying the authorisation with your bank. This usually takes a few seconds.
              </p>
              <div className="mt-10 inline-flex items-center gap-3 text-mocha">
                <Clock3 size={16} strokeWidth={1.25} className="animate-pulse" />
                <span className="text-[11px] tracking-[0.24em] uppercase">Checking payment…</span>
              </div>
            </>
          )}

          {state.kind === "success" && (
            <>
              <Eyebrow index="—" color="text-espresso">Confirmed</Eyebrow>
              <h1 className="mt-8 font-serif text-6xl md:text-7xl leading-[0.9] tracking-editorial">
                You&rsquo;re <span className="italic text-espresso">in.</span>
              </h1>
              <p className="mt-6 font-serif italic text-3xl text-mocha">
                {state.booking.salon} &middot; {state.booking.appointment_at ? formatAppointment(state.booking.appointment_at) : state.booking.time}
              </p>
              <div className="mt-10 paper rounded-sm p-6 md:p-7 max-w-lg">
                <div className="flex items-center justify-between border-b border-sand pb-4">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-mocha">Service</span>
                  <span className="font-serif text-lg text-ink">{state.booking.service}</span>
                </div>
                <div className="flex items-center justify-between border-b border-sand py-4">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-mocha">Authorised</span>
                  <span className="font-serif italic text-xl text-espresso">
                    ${state.booking.amount.toFixed(2)} {state.booking.currency.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-4">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-mocha">Status</span>
                  <span className="inline-flex items-center gap-2 text-sm text-espresso">
                    <CheckCircle2 size={14} strokeWidth={1.5} /> {state.booking.status === "confirmed" ? "Payment confirmed" : "Hold placed · captured on arrival"}
                  </span>
                </div>
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-3 rounded-full bg-espresso text-bone px-7 py-3.5 text-[11px] tracking-[0.26em] uppercase hover:scale-[1.03] transition-transform"
                  data-testid="booking-done-home"
                >
                  Explore more openings <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
                <a
                  href="#"
                  className="inline-flex items-center gap-3 text-[11px] tracking-[0.26em] uppercase text-espresso link-underline"
                  data-testid="booking-done-calendar"
                >
                  Add to calendar
                </a>
              </div>
              <p
                className="mt-10 text-[12px] text-ink/55 leading-relaxed max-w-lg"
                data-testid="booking-confirmed-policy"
              >
                {CANCEL_POLICY_SHORT}{" "}
                <Link
                  to="/terms#cancellations"
                  className="underline hover:text-espresso"
                  data-testid="booking-confirmed-terms-link"
                >
                  Read full Terms
                </Link>.
              </p>
            </>
          )}

          {state.kind === "expired" && (
            <>
              <Eyebrow index="—">Expired</Eyebrow>
              <h1 className="mt-8 font-serif text-6xl md:text-7xl leading-[0.9]">
                This reservation <span className="italic text-espresso">timed out.</span>
              </h1>
              <p className="mt-6 text-ink/70 max-w-lg">Nothing was charged. Try the opening again — it may still be available.</p>
              <Link to="/" className="mt-10 inline-flex items-center gap-3 rounded-full bg-espresso text-bone px-7 py-3.5 text-[11px] tracking-[0.26em] uppercase">
                Return home
              </Link>
            </>
          )}

          {state.kind === "timeout" && (
            <>
              <Eyebrow index="—">Pending</Eyebrow>
              <h1 className="mt-8 font-serif text-5xl md:text-6xl leading-[0.9]">
                Taking longer than usual.
              </h1>
              <p className="mt-6 text-ink/70 max-w-lg">We&rsquo;ll email you as soon as your hold is confirmed. You can safely close this tab.</p>
              <Link to="/" className="mt-10 inline-flex items-center gap-3 rounded-full bg-espresso text-bone px-7 py-3.5 text-[11px] tracking-[0.26em] uppercase">
                Return home
              </Link>
            </>
          )}

          {state.kind === "error" && (
            <>
              <Eyebrow index="—">Error</Eyebrow>
              <h1 className="mt-8 font-serif text-5xl leading-[0.95] inline-flex items-center gap-4">
                <CircleAlert size={36} strokeWidth={1.25} className="text-mocha" />
                Something went quiet.
              </h1>
              <p className="mt-6 text-ink/70 max-w-lg">{state.message}</p>
              <Link to="/" className="mt-10 inline-flex items-center gap-3 rounded-full bg-espresso text-bone px-7 py-3.5 text-[11px] tracking-[0.26em] uppercase">
                Return home
              </Link>
            </>
          )}
        </div>

        <div className="lg:col-span-5 reveal" style={{ animationDelay: "200ms" }}>
          <div className="aspect-[4/5] overflow-hidden bg-sand">
            <img src={HERO} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </div>
  );
}
